import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import "../../EngagementIncentives.css";
import { useSearchParams } from "react-router-dom";

interface Props {
  tableHeadings: { [key: string]: string };
  exequteQueryContentQuery5: any;
  brandName: any;
  sortedRows: any;
  setSortedRows: any;
}

const ClickDataTable = ({
  tableHeadings,
  exequteQueryContentQuery5,
  brandName,
  sortedRows,
  setSortedRows,
}: Props) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<string>("");
  const rows = exequteQueryContentQuery5?.query_output || [];
  const [searchParams, setSearchParams] = useSearchParams();
  const prevBrandNameRef = useRef(brandName);
  const headCells = Object.entries(tableHeadings);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const filteredRows =
    brandName.length !== 0
      ? rows.filter(
          (row: any) =>
            row.brand_name?.toLowerCase() === brandName.toLowerCase()
        )
      : rows;

  useEffect(() => {
    // Only reset if brandName actually changed
    if (prevBrandNameRef.current !== brandName) {
      setPage(0);
      prevBrandNameRef.current = brandName;
    }
  }, [brandName]);

  useEffect(() => {
    setSortedRows(
      filteredRows.sort((a: any, b: any) => {
        if (orderBy) {
          const aValue = a[orderBy];
          const bValue = b[orderBy];

          if (aValue === undefined || aValue === null)
            return order === "asc" ? 1 : -1;
          if (bValue === undefined || bValue === null)
            return order === "asc" ? -1 : 1;

          if (order === "asc") {
            return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
          } else {
            return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
          }
        }
        return 0;
      })
    );
    // eslint-disable-next-line
  }, [filteredRows, orderBy, order]);

  useEffect(() => {
    const pageParam = searchParams.get("pageClickData");
    // Ensure valid number and non-negative value
    setPage(Number(pageParam) || 0);
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <TableContainer>
        <Table aria-label="collapsible table" size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {headCells.map(([key, value], index) => (
                <TableCell key={index} align="left">
                  <TableSortLabel
                    active={orderBy === key}
                    direction={orderBy === key ? order : "asc"}
                    onClick={() => handleRequestSort(key)}
                  >
                    {value && typeof value === "string" ? value : ""}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row: any, rowIndex: any) => (
                <React.Fragment key={rowIndex}>
                  <TableRow
                    sx={{
                      "& > *": {
                        borderBottom:
                          "1px solid rgba(224, 224, 224, 1) !important",
                      },
                    }}
                  >
                    {headCells.map(([key], cellIndex) => (
                      <TableCell key={cellIndex} align="left">
                        {key === "date_of_aquisition" ? (
                          <>{row[key] ? row[key] : "-"}</>
                        ) : key === "brand_name" ? (
                          <> {row[key] ? row[key] : "-"}</>
                        ) : key === "campaign_id" ? (
                          <> {row[key] ? row[key] : "-"}</>
                        ) : key === "incentive_click_id" ? (
                          <>{row[key] ? row[key] : "-"}</>
                        ) : key === "optival_click_id" ? (
                          <>{row[key] ? row[key] : "-"}</>
                        ) : key === "qualified_ftd_date" ? (
                          <>{row[key] ? row[key] : "-"}</>
                        ) : key === "deposit_amount" ? (
                          <>{row[key] ? `${row[key]} $` : "0"}</>
                        ) : row[key] ? (
                          row[key]
                        ) : (
                          "0"
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </React.Fragment>
              ))}
          </TableBody>
        </Table>
        <Stack
          spacing={1}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={filteredRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                {
                  display: "none",
                },
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-toolbar": {
                paddingLeft: "16px",
              },
            }}
          />
          <Pagination
            count={Math.ceil(filteredRows.length / rowsPerPage)}
            page={page + 1}
            onChange={(_, value) => {
              const newPage = value - 1;
              setPage(newPage);
              setSearchParams((prevParams) => {
                const updatedParams = new URLSearchParams(prevParams);
                // ✅ Update 'page' param
                updatedParams.set("pageClickData", newPage.toString());
                return updatedParams;
              });
            }}
            showFirstButton
            showLastButton
            className="pagination-button"
            color="secondary"
            size="small"
            sx={{
              ".css-wjh20t-MuiPagination-ul": {
                justifyContent: "center",
              },
            }}
          />
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={filteredRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            classes={{
              displayedRows: "hidden-displayed-rows", // Apply the custom class
            }}
            sx={{
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-input": {
                marginRight: "15px",
              },
            }}
          />
        </Stack>
      </TableContainer>
    </>
  );
};

export default ClickDataTable;
